/**
 * Period ordering and gap detection for the TB Aggregator's file list.
 *
 * A file states one of three things, and the list has to respect which:
 *   a month      — Mar-24
 *   a fiscal year — FY24
 *   a year to date — YTD-25
 *
 * Pure functions, kept out of the component so the arithmetic that decides
 * "nothing covers this period" is testable. A missed gap means a period
 * silently absent from the built TB, which is the failure this whole tool
 * exists to prevent.
 */

export interface Period {
  year: number;
  month: number;
}

/** What a file's period means. */
export type PeriodKind = "month" | "fy" | "ytd";

/** The minimum a file needs to be placed in the list. */
export interface RowFile {
  fileId: string;
  name: string;
  entity: string;
  period: Period | null;
  periodKind: PeriodKind;
}

export type DisplayRow<T extends RowFile> =
  | { kind: "file"; file: T; n: number; duplicate: boolean }
  | { kind: "gap"; label: string };

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Months as a single sortable integer, so gaps are simple arithmetic. */
export const monthOrdinal = (p: Period): number => p.year * 12 + p.month;

export function fromOrdinal(o: number): Period {
  const year = Math.floor((o - 1) / 12);
  return { year, month: o - year * 12 };
}

/** House format for a month: "Mar-24". */
export const periodLabel = (p: Period): string => `${MONTH_ABBR[p.month - 1]}-${String(p.year).slice(2)}`;

/** How a file's period reads, whichever kind it is. */
export function fileLabel(period: Period | null, kind: PeriodKind): string {
  if (!period) return "";
  const yy = String(period.year).slice(2);
  if (kind === "fy") return `FY${yy}`;
  if (kind === "ytd") return `YTD-${yy}`;
  return periodLabel(period);
}

/**
 * Gaps are only meaningful between two files measuring the same thing.
 * Months are compared against months, and years against years. A fiscal-year
 * file sitting between two months must never produce "no file for Aug-24".
 */
type Domain = "month" | "year";
const domainOf = (kind: PeriodKind): Domain => (kind === "month" ? "month" : "year");

/** Where a file sorts: months by their month, years by the month they end in. */
function sortOrdinal(f: RowFile, fyEndMonth: number): number {
  if (!f.period) return Number.POSITIVE_INFINITY;
  if (f.periodKind === "month") return monthOrdinal(f.period);
  return monthOrdinal({ year: f.period.year, month: fyEndMonth });
}

/** The integer gaps are counted in: month ordinal, or fiscal year. */
function gapIndex(f: RowFile, fyEndMonth: number): number {
  if (!f.period) return Number.POSITIVE_INFINITY;
  return f.periodKind === "month" ? monthOrdinal(f.period) : f.period.year;
}

function gapLabel(domain: Domain, index: number): string {
  return domain === "month" ? periodLabel(fromOrdinal(index)) : `FY${String(index).slice(2)}`;
}

/**
 * One row per file in period order, with an explicit marker wherever a period
 * is missing from a run. Sorting alone makes a gap findable; a row that says
 * "Mar-24 — no file" makes it unmissable, which is the point.
 *
 * Files with no period yet sort last, since those are the ones waiting on
 * input. A gap is only drawn between two dated files of the same entity AND
 * the same kind — never trailing off the end, where we have no idea how many
 * periods were meant to follow.
 *
 * Only the one-entity-per-file mode groups by a typed name; when entity names
 * come out of the files the batch is a single run.
 */
export function buildDisplayRows<T extends RowFile>(
  files: T[],
  entityMode: "single" | "spread",
  fyEndMonth = 12,
): DisplayRow<T>[] {
  const groupOf = (f: T) => (entityMode === "single" ? f.entity.trim().toLowerCase() : "");

  const sorted = [...files].sort((a, b) => {
    const g = groupOf(a).localeCompare(groupOf(b));
    if (g !== 0) return g;
    const oa = sortOrdinal(a, fyEndMonth);
    const ob = sortOrdinal(b, fyEndMonth);
    if (oa !== ob) return oa - ob;
    return a.name.localeCompare(b.name, undefined, { numeric: true });
  });

  // Count files per (entity, kind, period) so a doubled-up period is flagged.
  const seen = new Map<string, number>();
  const dupKey = (f: T) => `${groupOf(f)}|${domainOf(f.periodKind)}|${gapIndex(f, fyEndMonth)}`;
  for (const f of sorted) {
    if (!f.period) continue;
    seen.set(dupKey(f), (seen.get(dupKey(f)) ?? 0) + 1);
  }

  const out: DisplayRow<T>[] = [];
  let prev: { group: string; domain: Domain; index: number } | null = null;
  let n = 0;
  for (const f of sorted) {
    const group = groupOf(f);
    const domain = domainOf(f.periodKind);
    const index = gapIndex(f, fyEndMonth);
    if (prev && prev.group === group && prev.domain === domain && Number.isFinite(index) && Number.isFinite(prev.index)) {
      for (let m = prev.index + 1; m < index; m++) out.push({ kind: "gap", label: gapLabel(domain, m) });
    }
    const duplicate = !!f.period && (seen.get(dupKey(f)) ?? 0) > 1;
    out.push({ kind: "file", file: f, n: ++n, duplicate });
    if (f.period) prev = { group, domain, index };
  }
  return out;
}

// ── Month/year picker state ────────────────────────────────────────────────
//
// A period needs both halves, but the analyst can only enter one at a time.
// Holding the half-entered state is the whole job here: commit only when
// both are present, and never let an empty value from the parent wipe out
// the half already typed.

export interface PeriodDraft {
  month: number | null;
  year: number | null;
}

export const draftFrom = (p: Period | null): PeriodDraft => ({ month: p?.month ?? null, year: p?.year ?? null });

/** A draft becomes a real period only once both halves are set. */
export function commitDraft(d: PeriodDraft): Period | null {
  return d.month && d.year ? { year: d.year, month: d.month } : null;
}

/**
 * Fold an incoming value from the parent into the draft. A null value means
 * "nothing committed yet", which must NOT erase a half-entered draft — that
 * was the bug that made the picker impossible to use.
 */
export function syncDraft(prev: PeriodDraft, value: Period | null): PeriodDraft {
  return value ? { month: value.month, year: value.year } : prev;
}

/** Which half is still missing, for the hint beside the dropdowns. */
export function draftMissing(d: PeriodDraft): "month" | "year" | null {
  if (d.month && !d.year) return "year";
  if (d.year && !d.month) return "month";
  return null;
}

/** Span, gap count and undated count for the line above the table. */
export function coverageSummary<T extends RowFile>(
  files: T[],
  rows: DisplayRow<T>[],
  fyEndMonth = 12,
): { span: string; gaps: number; undated: number } {
  const dated = files.filter((f) => f.period);
  const undated = files.length - dated.length;
  const gaps = rows.filter((r) => r.kind === "gap").length;
  if (dated.length === 0) return { span: "", gaps, undated };

  const withOrd = dated.map((f) => ({ f, o: sortOrdinal(f, fyEndMonth) }));
  withOrd.sort((a, b) => a.o - b.o);
  const lo = withOrd[0].f;
  const hi = withOrd[withOrd.length - 1].f;
  const loLabel = fileLabel(lo.period, lo.periodKind);
  const hiLabel = fileLabel(hi.period, hi.periodKind);
  return { span: loLabel === hiLabel ? loLabel : `${loLabel} to ${hiLabel}`, gaps, undated };
}
